import { useTheme, Drawer as CoreDrawer, DrawerProps } from "@mui/material";
import { useConfig } from "../../../contexts/ConfigContext";

interface DrawerPropsType extends DrawerProps {
  isOpen?: boolean;
  isFullscreenMode?: boolean;
  anchorPosition?: "left" | "right";
  isCompactMode?: boolean;
}

export default function Drawer({
  isOpen = false,
  anchorPosition,
  onClose,
  variant = "persistent",
  isFullscreenMode,
  isCompactMode,
  children,
  ...restProps
}: DrawerPropsType) {
  const config = useConfig();
  const theme = useTheme();

  const getBackdropStyle = () => {
    if (isFullscreenMode) {
      // Reduce opacity in Tables mode to show more of the map underneath
      const baseOpacity = config.backgroundOpacity || 0.4;
      const tablesOpacity = baseOpacity * 0.6; // 60% of original opacity for Tables mode
      const opacity = Math.round(tablesOpacity * 255)
        .toString(16)
        .padStart(2, "0");
      return {
        style: {
          backgroundColor: `${theme.palette.secondary.main}${opacity}` // Use theme secondary color with reduced opacity
        }
      };
    }
    return {
      style: {
        backgroundColor: "transparent"
      }
    };
  };

  const getBackgroundStyle = () => {
    if (isFullscreenMode) {
      // Reduce opacity in Tables mode to show more of the map underneath
      const baseOpacity = config.backgroundOpacity || 0.4;
      const tablesOpacity = baseOpacity * 0.6; // 60% of original opacity for Tables mode
      const opacity = Math.round(tablesOpacity * 255)
        .toString(16)
        .padStart(2, "0");
      return `${theme.palette.secondary.main}${opacity}`; // Use theme secondary color with reduced opacity
    }
    return isCompactMode
      ? "radial-gradient(circle at center, rgba(30, 30, 30, 0.85) 0%, rgba(20, 20, 20, 0.90) 40%, rgba(10, 10, 10, 0.95) 80%, rgba(0, 0, 0, 0.98) 100%)" // Central vignette burst
      : "transparent";
  };

  return (
    <CoreDrawer
      {...restProps}
      open={isOpen}
      onClose={onClose}
      BackdropProps={getBackdropStyle()}
      PaperProps={{
        ...restProps.PaperProps,
        style: {
          position: isFullscreenMode ? "fixed" : "absolute",
          top: 66,
          display: "flex",
          flexDirection: "column",
          left: isFullscreenMode ? "0" : "auto",
          background: getBackgroundStyle(),
          backdropFilter: isCompactMode ? "blur(10px)" : "none", // No blur in Tables mode for clear map visibility
          // border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.2)"}`,
          // boxShadow: theme.palette.mode === "dark" ? "0 8px 32px 0 rgba(0, 0, 0, 0.37)" : "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
          transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)", // Smooth eased transition
          zIndex: 1100,
          overflow: "hidden",
          pointerEvents: isCompactMode ? "auto" : "none",
          ...restProps.PaperProps?.style
        }
      }}
    >
      {children}
    </CoreDrawer>
  );
}
